import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ThemeService } from 'src/app/services/theme-service/theme.service';
import { FontResizeService } from '../services/font-resize/font-resize.service';

@Component({
    selector: 'app-app-accessibility-panel',
    templateUrl: './app-accessibility-panel.component.html',
    styleUrls: ['./app-accessibility-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppAccessibilityPanelComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private themeService: ThemeService,
    public fontService: FontResizeService
  ) { }

  private headerObserver?: ResizeObserver;
  private readonly syncFabPosition = () => this.updateFabVerticalPosition();
  /** True when Ctrl+Shift+Enter/Tab already toggled — ignore solo Ctrl/Shift keyup. */
  private shortcutComboUsed = false;
  /** True when Ctrl/Shift was used with another key (e.g. Ctrl+C) — do not toggle on release. */
  private modifierUsedWithOtherKey = false;

  panelOpen = false;
  highContrast = false;
  accessibility: any = null;

  themes = [
    { value: 'theme-blue', label: 'Blue', bg: '#d6e4f7', border: '#a8c5e8' },
    { value: 'theme-yellow', label: 'Yellow', bg: '#f5f0c0', border: '#e0d88a' },
    { value: 'theme-green', label: 'Green', bg: '#c8ecd4', border: '#8ecfa0' },
    { value: 'theme-pink', label: 'Pink', bg: '#f5c6cb', border: '#e8939c' },
  ];

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('keyup', this.handleKeyup);
  }

  ngAfterViewInit(): void {
    this.updateFabVerticalPosition();
    const header = document.querySelector('.header-nav');
    if (header && typeof ResizeObserver !== 'undefined') {
      this.headerObserver = new ResizeObserver(() => this.updateFabVerticalPosition());
      this.headerObserver.observe(header);
    }
    window.addEventListener('resize', this.syncFabPosition);
    setTimeout(() => this.updateFabVerticalPosition(), 300);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('keyup', this.handleKeyup);
    window.removeEventListener('resize', this.syncFabPosition);
    this.headerObserver?.disconnect();
  }

  private updateFabVerticalPosition(): void {
    const root = document.documentElement;
    if (window.innerWidth > 990) {
      root.style.removeProperty('--accessibility-fab-top');
      root.style.removeProperty('--accessibility-panel-top');
      root.style.removeProperty('--mobile-header-bottom');
      return;
    }

    const header = document.querySelector('.header-nav') as HTMLElement | null;
    const fab = document.querySelector('.accessibility-fab') as HTMLElement | null;
    if (!header) {
      return;
    }

    const headerRect = header.getBoundingClientRect();
    root.style.setProperty('--mobile-header-bottom', `${headerRect.bottom}px`);

    if (!fab) {
      return;
    }

    const fabHeight = fab.getBoundingClientRect().height || fab.offsetHeight || 48;
    const top = headerRect.top + headerRect.height / 2 - fabHeight / 2;

    root.style.setProperty('--accessibility-fab-top', `${top}px`);
    root.style.setProperty('--accessibility-panel-top', `${top + fabHeight + 8}px`);
  }

  closePanel(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.panelOpen = false;
  }

  togglePanel(event?: Event) {
    event?.stopPropagation();
    this.panelOpen = !this.panelOpen;
  }

  toggleContrast() {
    const currentTheme = localStorage.getItem('app-theme') || 'theme-light';
    const newTheme = currentTheme === 'theme-light' ? 'theme-dark' : 'theme-light';
    this.setTheme(newTheme);
  }

  onFontChange(event: any) {
    const val = parseInt(event.target.value, 10);
    if (this.fontService.fontStep === undefined || this.fontService.fontStep === null) return;
    if (val > this.fontService.fontStep) {
      this.increase();
    } else if (val < this.fontService.fontStep) {
      this.decrease();
    }
  }

  resetAll() {
    this.fontService.reset();
    this.setTheme('theme-light');
    this.fontService.stopSpeech();
  }

  setTheme(theme: string) {
    this.themeService.setTheme(theme);
  }

  increase() { this.fontService.increase(); }
  decrease() { this.fontService.decrease(); }
  reset() { this.fontService.reset(); }

  toggleScreenReader() {
    this.fontService.toggleScreenReader();
  }

  private isTypingTarget(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el) {
      return false;
    }
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
      return true;
    }
    return el.isContentEditable;
  }

  handleKeydown = (e: KeyboardEvent) => {
    if (e.repeat || this.isTypingTarget()) {
      return;
    }

    // Ctrl/Shift held with any other key → not a solo modifier tap
    if (
      (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) &&
      e.key !== 'Control' &&
      e.key !== 'Shift' &&
      e.key !== 'Alt' &&
      e.key !== 'Meta'
    ) {
      this.modifierUsedWithOtherKey = true;
    }
    if ((e.key === 'Shift' && e.ctrlKey) || (e.key === 'Control' && e.shiftKey)) {
      this.modifierUsedWithOtherKey = true;
    }

    // Combo: Ctrl+Shift+Enter or Ctrl+Shift+Tab → one toggle (no blink)
    if (e.ctrlKey && e.shiftKey && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      this.shortcutComboUsed = true;
      this.panelOpen = !this.panelOpen;
      return;
    }

    // Solo Tab / Enter
    if (
      (e.key === 'Tab' || e.key === 'Enter') &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      this.panelOpen = !this.panelOpen;
      return;
    }

    if (e.key === 'Escape' && this.panelOpen) {
      this.panelOpen = false;
    }
  };

  handleKeyup = (e: KeyboardEvent) => {
    if (this.isTypingTarget()) {
      return;
    }

    if (e.key !== 'Control' && e.key !== 'Shift') {
      return;
    }

    // Wait until both modifiers are released before deciding
    if (e.ctrlKey || e.shiftKey) {
      return;
    }

    if (this.shortcutComboUsed || this.modifierUsedWithOtherKey) {
      this.shortcutComboUsed = false;
      this.modifierUsedWithOtherKey = false;
      return;
    }

    // Solo Ctrl or Shift tap toggles open/close
    this.panelOpen = !this.panelOpen;
  };
}
